import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { TextInput } from "../../../../form/textInput";
import { Switch } from "../../../../form/switch";
import { Loader, Main, Styled } from "./style";
import { webURL } from "../../../../../services/API";
import axios from "axios";
import { AuthenticateSelect } from "../../../../auth/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";

type OutlookFolder = {
  id: string;
  displayName: string;
  parentFolderId?: string;
  childFolderCount?: number;
  totalItemCount?: number;
  unreadItemCount?: number;
};

type OutlookNewEmailTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

function generateClientState() {
  return `outlook_${Math.random().toString(36).slice(2, 12)}_${Date.now()}`;
}

function formatCountdown(msRemaining: number) {
  if (msRemaining <= 0) return "Expired";
  const totalSeconds = Math.floor(msRemaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m ${seconds}s`;
}

function normalizeTriggerError(error: any) {
  const payload = error?.response?.data ?? error?.message ?? "Unknown error";
  const text = typeof payload === "string" ? payload : JSON.stringify(payload);

  if (
    /Mail\.Read/i.test(text) ||
    /admin consent/i.test(text) ||
    /consent/i.test(text)
  ) {
    return "Admin consent required (Mail.Read Application)";
  }
  if (/AccessDenied/i.test(text)) {
    return `AccessDenied: ${text}`;
  }
  return text;
}

export function OutlookTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: OutlookNewEmailTriggerProps) {
  const MAX_SUBSCRIPTION_MINUTES = 10070; // Graph limit
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [statusText, setStatusText] = React.useState<string>("");
  const [countdown, setCountdown] = React.useState<string>("");
  const [searchParams] = useSearchParams();
  const tokenRef = React.useRef<string>("");
  const projectId = searchParams.get("id");
  const [folders, setFolders] = React.useState<OutlookFolder[]>([]);
  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [folderError, setFolderError] = React.useState("");
  const hasAnyFilters = React.useMemo(
    () =>
      !!data.value?.filterQuery ||
      !!data.value?.hasAttachmentsFilter ||
      (data.value?.readStatus && data.value.readStatus !== "all") ||
      !!data.value?.sender ||
      (data.value?.foldersInclude || []).length > 0 ||
      (data.value?.foldersExclude || []).length > 0,
    [
      data.value?.filterQuery,
      data.value?.hasAttachmentsFilter,
      data.value?.readStatus,
      data.value?.sender,
      data.value?.foldersInclude,
      data.value?.foldersExclude,
    ]
  );
  const [showFilters, setShowFilters] = React.useState<boolean>(hasAnyFilters);

  const latestValueRef = React.useRef<any>(data.value);
  React.useEffect(() => {
    latestValueRef.current = data.value;
  }, [data.value]);

  React.useEffect(() => {
    const bearer =
      data.value?.connection?.access_token ||
      data.value?.connection?.accessToken ||
      "";
    if (bearer) {
      tokenRef.current = bearer;
    }
  }, [data.value?.connection]);

  React.useEffect(() => {
    if (setChildMinimize) {
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    }
    if (setChildSettings) {
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        folder: "Inbox",
        changeType: "created,updated",
        includeAttachments: false,
        hasAttachmentsFilter: false,
        filterQuery: "",
        readStatus: "all",
        sender: "",
        foldersInclude: [],
        foldersExclude: [],
        expirationDateTime: new Date(
          Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000
        ).toISOString(),
      });
      const initialMsRemaining = MAX_SUBSCRIPTION_MINUTES * 60 * 1000;
      setCountdown(formatCountdown(initialMsRemaining));
      return;
    }

    if (!data.value.folder) updateNodeValue(id, setNodes, { folder: "Inbox" });
    if (!data.value.changeType)
      updateNodeValue(id, setNodes, { changeType: "created,updated" });
    if (data.value.includeAttachments === undefined) {
      updateNodeValue(id, setNodes, { includeAttachments: false });
    }
    if (data.value.hasAttachmentsFilter === undefined) {
      updateNodeValue(id, setNodes, { hasAttachmentsFilter: false });
    }
    if (!data.value.readStatus)
      updateNodeValue(id, setNodes, { readStatus: "all" });
    if (!data.value.foldersInclude)
      updateNodeValue(id, setNodes, { foldersInclude: [] });
    if (!data.value.foldersExclude)
      updateNodeValue(id, setNodes, { foldersExclude: [] });
    if (data.value.filterQuery === undefined)
      updateNodeValue(id, setNodes, { filterQuery: "" });
    if (data.value.sender === undefined)
      updateNodeValue(id, setNodes, { sender: "" });
    if (!data.value.expirationDateTime) {
      updateNodeValue(id, setNodes, {
        expirationDateTime: new Date(
          Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000
        ).toISOString(),
      });
      const initialMsRemaining = MAX_SUBSCRIPTION_MINUTES * 60 * 1000;
      setCountdown(formatCountdown(initialMsRemaining));
    }

    if (!data.value.clientState) {
      updateNodeValue(id, setNodes, { clientState: generateClientState() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!data.output) return;
    updateNodeValue(id, setNodes, { lastReceivedAt: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.output]);

  React.useEffect(() => {
    const expiration = data.value?.expirationDateTime;
    if (!expiration) {
      setCountdown("");
      return;
    }
    const tick = () => {
      const msRemaining = new Date(expiration).getTime() - Date.now();
      setCountdown(formatCountdown(msRemaining));
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [data.value?.expirationDateTime]);

  const currentWebhookToken = projectId && id ? `${projectId}/${id}` : "";
  const isSubscriptionForThisNode = currentWebhookToken
    ? (data.value?.notificationUrl || "").includes(currentWebhookToken)
    : true;

  const isConnected =
    !!data.value?.subscriptionId &&
    !!data.value?.expirationDateTime &&
    new Date(data.value.expirationDateTime).getTime() > Date.now() &&
    data.value?.status !== "disabled" &&
    isSubscriptionForThisNode;

  // const hasConnection = !!data.value?.connection;
  // const isWarning = !hasConnection || !isConnected;

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
  };

  const handleAuthenticate = async (type: "add" | "remove", authData: any) => {
    if (type === "add") {
      const payload: Record<string, any> = { connection: authData };
      if (authData?.email) payload.mailboxEmail = authData.email;
      console.log("Authenticated with Microsoft:", authData.access_token);
      tokenRef.current = authData.access_token || "";
      updateNodeValue(id, setNodes, payload);
    } else if (type === "remove") {
      tokenRef.current = "";
      updateNodeValue(id, setNodes, {
        connection: null,
        subscriptionId: "",
        expirationDateTime: "",
        status: "disabled",
        lastError: null,
      });
    }
  };

  const buildAuthHeaders = (value: any) => {
    const bearer =
      tokenRef.current ||
      value?.connection?.access_token ||
      value?.connection?.accessToken ||
      "";
    return bearer ? { Authorization: `Bearer ${bearer}` } : undefined;
  };

  const isValidEmail = (value?: string) =>
    !value || /^\S+@\S+\.\S+$/.test(value.trim());

  const handleToggleFolder = (
    type: "include" | "exclude",
    folderId: string
  ) => {
    const key = type === "include" ? "foldersInclude" : "foldersExclude";
    const otherKey = type === "include" ? "foldersExclude" : "foldersInclude";
    const current: string[] = Array.isArray(latestValueRef.current?.[key])
      ? [...latestValueRef.current[key]]
      : [];
    const other: string[] = Array.isArray(latestValueRef.current?.[otherKey])
      ? [...latestValueRef.current[otherKey]]
      : [];

    if (current.includes(folderId)) {
      updateNodeValue(id, setNodes, {
        [key]: current.filter((id) => id !== folderId),
      });
      return;
    }

    const nextCurrent = [...current, folderId];
    const nextOther = other.filter((id) => id !== folderId);
    updateNodeValue(id, setNodes, {
      [key]: nextCurrent,
      [otherKey]: nextOther,
    });
  };

  const handleIncludeAllFolders = () => {
    if (!folders.length) return;
    const allIds = folders.map((f) => f.id);
    updateNodeValue(id, setNodes, {
      foldersInclude: allIds,
      foldersExclude: [],
    });
  };

  const allFoldersIncluded =
    folders.length > 0 &&
    (data.value?.foldersInclude?.length || 0) === folders.length &&
    (data.value?.foldersExclude?.length || 0) === 0;

  const maxExpirationDate = React.useCallback(
    () => new Date(Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000),
    []
  );

  const handleEnable = async (mode: "activate" | "renew" = "activate") => {
    if (!projectId) {
      setStatusText("Project ID not found in URL");
      return;
    }

    const value = latestValueRef.current || {};
    if (!value?.connection) {
      setStatusText("Please authenticate with Microsoft first.");
      return;
    }
    if (!value.mailboxEmail) {
      setStatusText("Mailbox email is required.");
      return;
    }
    if (!isValidEmail(value.mailboxEmail)) {
      setStatusText("Mailbox email is not a valid email address.");
      return;
    }
    if (!isValidEmail(value.sender)) {
      setStatusText("Sender email is not a valid email address.");
      return;
    }

    if (!value.clientState) {
      const newClientState = generateClientState();
      updateNodeValue(id, setNodes, { clientState: newClientState });
    }

    setIsLoading(true);
    setStatusText(
      mode === "renew"
        ? "Renewing subscription..."
        : "Activating subscription..."
    );

    try {
      const headers = buildAuthHeaders(value);
      const cap = maxExpirationDate();
      let expirationDateTime = value.expirationDateTime;
      if (
        expirationDateTime &&
        new Date(expirationDateTime).getTime() > cap.getTime()
      ) {
        expirationDateTime = cap.toISOString();
      }

      // Best-effort cleanup to avoid stacking multiple subscriptions
      if (value.subscriptionId) {
        console.log("Removing existing subscription before create...");
        try {
          await axios.post(
            `${webURL}/triggers/outlook/subscription/disable`,
            {
              projectId,
              nodeId: id,
              subscriptionId: value.subscriptionId,
            },
            headers ? { headers } : undefined
          );
        } catch (cleanupErr) {
          console.warn(
            "Failed to remove existing subscription before create",
            cleanupErr
          );
        }
      }

      const response = await axios.post(
        `${webURL}/triggers/outlook/subscription/enable`,
        {
          projectId,
          nodeId: id,
          mailboxEmail: value.mailboxEmail,
          folder: value.folder || "Inbox",
          changeType: value.changeType || "created,updated",
          includeAttachments: !!value.includeAttachments,
          tenantId: value.tenantId,
          clientId: value.clientId,
          clientSecret: value.clientSecret,
          clientState: value.clientState,
          subscriptionId: value.subscriptionId || undefined,
          connection: value.connection,
          expirationDateTime: expirationDateTime || undefined,
          filterQuery: value.filterQuery || "",
          hasAttachmentsFilter: !!value.hasAttachmentsFilter,
          readStatus: value.readStatus || "all",
          sender: value.sender || "",
          foldersInclude: value.foldersInclude || [],
          foldersExclude: value.foldersExclude || [],
        },
        headers ? { headers } : undefined
      );

      const payload = response.data || {};
      if (payload.connection) {
        updateNodeValue(id, setNodes, { connection: payload.connection });
      }

      updateNodeValue(id, setNodes, {
        subscriptionId: payload.subscriptionId ?? value.subscriptionId ?? "",
        expirationDateTime:
          payload.expirationDateTime ?? value.expirationDateTime ?? "",
        status: payload.status ?? "active",
        lastError: payload.lastError ?? null,
      });

      setStatusText("✅ Connected");
    } catch (error: any) {
      const message = normalizeTriggerError(error);
      updateNodeValue(id, setNodes, { status: "error", lastError: message });
      setStatusText(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!projectId) {
      setStatusText("Project ID not found in URL");
      return;
    }
    const value = latestValueRef.current || {};
    if (!value?.connection) {
      setStatusText("Please authenticate with Microsoft first.");
      return;
    }
    setIsLoading(true);
    setStatusText("Deactivating subscription...");

    try {
      const headers = buildAuthHeaders(value);
      await axios.post(
        `${webURL}/triggers/outlook/subscription/disable`,
        {
          projectId,
          nodeId: id,
          subscriptionId: value.subscriptionId || undefined,
        },
        headers ? { headers } : undefined
      );

      updateNodeValue(id, setNodes, {
        subscriptionId: "",
        expirationDateTime: "",
        status: "disabled",
        lastError: null,
      });
      setStatusText("✅ Disconnected");
    } catch (error: any) {
      const message = normalizeTriggerError(error);
      updateNodeValue(id, setNodes, { status: "error", lastError: message });
      setStatusText(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    const value = latestValueRef.current || {};
    setFolderError("");

    if (!value?.connection) {
      setFolderError("Please authenticate first.");
      return;
    }
    if (!value?.mailboxEmail) {
      setFolderError("Mailbox email is required to fetch folders.");
      return;
    }
    if (!isValidEmail(value.mailboxEmail)) {
      setFolderError("Mailbox email is not valid.");
      return;
    }

    setIsFoldersLoading(true);
    try {
      const headers = buildAuthHeaders(value);
      const response = await axios.post(
        `${webURL}/triggers/outlook/folders`,
        {
          mailboxEmail: value.mailboxEmail,
          connection: value.connection,
          tenantId: value.tenantId,
          clientId: value.clientId,
          clientSecret: value.clientSecret,
        },
        headers ? { headers } : undefined
      );
      const payload = response.data || {};
      const folderList: OutlookFolder[] = payload.folders || [];
      setFolders(folderList);
      if (payload.connection) {
        updateNodeValue(id, setNodes, { connection: payload.connection });
      }
    } catch (err: any) {
      setFolderError(normalizeTriggerError(err));
      setFolders([]);
    } finally {
      setIsFoldersLoading(false);
    }
  };

  React.useEffect(() => {
    if (!isConnected) return;

    const interval = window.setInterval(() => {
      const value = latestValueRef.current || {};
      if (!value?.subscriptionId) return;
      const hasDelegated = !!value?.connection;
      const hasAppCreds =
        !!value?.tenantId && !!value?.clientId && !!value?.clientSecret;
      if (!hasDelegated && !hasAppCreds) return;

      void handleEnable("renew");
    }, 30 * 60 * 1000);

    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);
  const isWarning = !data.value?.connection;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            >
            {isLoading ? (
              <Loader />
            ) : (
              <Styled>
                <AuthenticateSelect
                  type="microsoft"
                  defaultId={data.value?.connection?.id}
                  handleAuthenticate={handleAuthenticate}
                  background={data.background}
                />

                <div className="card">
                  <div className="cardHeader">
                    <h4>Connection</h4>
                    <span
                      className={`badge ${
                        isConnected ? "connected" : "disconnected"
                      }`}>
                      {isConnected ? "Connected" : "Not connected"}
                    </span>
                  </div>

                  <div className="statusGrid">
                    <div className="statusItem">
                      <span>Expires in</span>
                      <span>{countdown || "—"}</span>
                    </div>
                    <div className="statusItem">
                      <span>Last email</span>
                      <span>
                        {data.value?.lastReceivedAt
                          ? new Date(data.value.lastReceivedAt).toLocaleString()
                          : "—"}
                      </span>
                    </div>
                  </div>

                  {data.value?.expirationDateTime ? (
                    <p className="muted" style={{ textAlign: 'center' }}>
                      Expiration:{" "}
                      {new Date(data.value.expirationDateTime).toLocaleString()}
                    </p>
                  ) : null}
                </div>

                <div className="section">
                  <TextInput
                    keyRef="mailboxEmail"
                    value={data.value?.mailboxEmail || ""}
                    title="Your Mail"
                    placeholder="user@domain.com"
                    handleChange={handleChangeInput}
                  />
                  <br />
                  <Switch
                    title="Include Attachments"
                    value={!!data.value?.includeAttachments}
                    setValue={() =>
                      updateNodeValue(id, setNodes, {
                        includeAttachments: !data.value?.includeAttachments,
                      })
                    }
                  />
                </div>

                <div className="card">
                  <div className="section">
                    <div className="cardHeader">
                      { !showFilters ? <h4>Filters</h4> : null }
                      <button
                        type="button"
                        className="btn1 withBorder"
                        onClick={() => setShowFilters((prev) => !prev)}>
                        {showFilters ? "Hide filters" : "Show filters"}
                      </button>
                    </div>
                    {!showFilters ? <p className="muted" style={{ padding: '5px 0' }}>
                      Narrow which Outlook messages trigger this node. Combine
                      OData filters, read state, attachments, sender, and folder
                      scoping.
                    </p> : null }

                    {showFilters ? (
                      <div className="scroll">
                        <TextInput
                          keyRef="filterQuery"
                          value={data.value?.filterQuery || ""}
                          title="filters"
                          placeholder={`e.g. "isRead eq false"`}
                          handleChange={handleChangeInput}
                        />
                        <p className="muted">
                          Supports Graph message filters (e.g.,
                          categories/any(), importance eq 'high', isRead eq
                          false).
                        </p>

                        {/* <div className="rowSelect">
                          <Select
                            title="Read Status"
                            data={readStatusOptions}
                            handleSelectItem={(selectedId) => {
                              const selected = readStatusOptions.find(
                                (opt) => opt.id === selectedId
                              );
                              updateNodeValue(id, setNodes, {
                                readStatus: selected?.refId || "all",
                              });
                            }}
                            isSmall={true}
                            emptyMsg="Choose status"
                          />
                        </div>
                        <p className="muted">
                          Restrict to unread or already-read messages.
                        </p> */}

                        <TextInput
                          keyRef="sender"
                          value={data.value?.sender || ""}
                          title="Sender email"
                          placeholder="sender@example.com"
                          handleChange={handleChangeInput}
                        />
                        <p className="muted">
                          Optional; triggers only when the From address matches.
                        </p>
                        {/* <DateInput
                          keyRef="expirationDateTime"
                          value={formatDateForInput(data.value?.expirationDateTime || defaultExpirationIso)}
                          title="Subscription expiration (UTC)"
                          isWithTime
                          handleChange={handleChangeDateInput}
                        /> */}
                        {/* <p className="muted">
                          Choose when this subscription should expire/renew. Microsoft may cap the max duration.
                        </p> */}
                        <div
                          className="folderList"
                          style={{
                            background: "rgba(0,0,0,0.02)",
                            borderRadius: "12px",
                            padding: "12px",
                            border: "1px solid rgba(0,0,0,0.06)",
                          }}>
                          <div
                            className="cardHeader"
                            style={{
                              paddingLeft: 0,
                              paddingRight: 0,
                              marginBottom: "8px",
                            }}>
                            <h4 style={{ margin: 0 }}>Folders</h4>
                            <button
                              type="button"
                              className="btn1 withBorder"
                              onClick={fetchFolders}
                              disabled={isFoldersLoading}>
                              <FontAwesomeIcon
                                icon={faSync}
                                spin={isFoldersLoading}
                              />{" "}
                              Fetch folders
                            </button>
                            <button
                              type="button"
                              className={`btn1 withBorder ${
                                allFoldersIncluded ? "active" : ""
                              }`}
                              onClick={handleIncludeAllFolders}
                              disabled={!folders.length}>
                              Include all
                            </button>
                          </div>

                          {isFoldersLoading || folders.length > 0 ? (
                            <>
                              {folderError ? (
                                <div
                                  className="statusText error"
                                  style={{ marginBottom: "8px" }}>
                                  {folderError}
                                </div>
                              ) : null}

                              {isFoldersLoading && folders.length === 0 ? (
                                <div className="muted">Loading folders…</div>
                              ) : null}

                              {!isFoldersLoading && !folders.length ? (
                                <div className="muted">
                                  No folders loaded yet. Fetch to browse and
                                  add.
                                </div>
                              ) : null}

                              {folders.length > 0 ? (
                                <div
                                  style={{
                                    maxHeight: "260px",
                                    overflow: "auto",
                                    display: "grid",
                                    gap: "8px",
                                  }}>
                                  {folders.map((folder) => (
                                    <div
                                      key={folder.id}
                                      className="folderRow"
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr auto",
                                        gap: "8px",
                                        alignItems: "center",
                                        padding: "10px",
                                        borderRadius: "10px",
                                        background: "#fff",
                                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                                      }}>
                                      <div style={{ minWidth: 0 }}>
                                        <div
                                          style={{
                                            fontWeight: 600,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                          }}>
                                          {folder.displayName || "(no name)"}
                                        </div>
                                        <div
                                          className="muted"
                                          style={{
                                            fontSize: "12px",
                                            wordBreak: "break-all",
                                          }}>
                                          {folder.totalItemCount !== undefined
                                            ? `total ${
                                                folder.totalItemCount
                                              }, unread ${
                                                folder.unreadItemCount ?? 0
                                              }`
                                            : ""}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "6px",
                                          justifyContent: "flex-end",
                                        }}>
                                        {(() => {
                                          const isIncluded = (
                                            data.value?.foldersInclude || []
                                          ).includes(folder.id);
                                          const isExcluded = (
                                            data.value?.foldersExclude || []
                                          ).includes(folder.id);
                                          return (
                                            <>
                                              <button
                                                type="button"
                                                className={`btn1 withBorder ${
                                                  isIncluded ? "active" : ""
                                                }`}
                                                onClick={() =>
                                                  handleToggleFolder(
                                                    "include",
                                                    folder.id
                                                  )
                                                }>
                                                {isIncluded
                                                  ? "Included"
                                                  : "Include"}
                                              </button>
                                              <button
                                                type="button"
                                                className={`btn1 withBorder ${
                                                  isExcluded ? "active" : ""
                                                }`}
                                                onClick={() =>
                                                  handleToggleFolder(
                                                    "exclude",
                                                    folder.id
                                                  )
                                                }>
                                                {isExcluded
                                                  ? "Excluded"
                                                  : "Exclude"}
                                              </button>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  { !showFilters ? <div className="buttons">
                    <button
                      className="btn1 withBorder"
                      onClick={() => handleEnable("activate")}
                      disabled={isLoading}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />{" "}
                      Activate
                    </button>
                    {/* <button
                      className="btn1 withBorder"
                      onClick={() => handleEnable("renew")}
                      disabled={isLoading || 
                      !data.value?.subscriptionId
                      }>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} /> Renew
                    </button> */}
                    <button
                      className="btn1 withBorder"
                      onClick={handleDisable}
                      // disabled={isLoading || !data.value?.subscriptionId}
                    >
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />{" "}
                      Deactivate
                    </button>
                  </div> : null}
                  {statusText ? (
                    <div
                      className={`statusText ${
                        statusText.includes("✅")
                          ? "success"
                          : statusText.toLowerCase().includes("error")
                          ? "error"
                          : ""
                      }`}>
                      {statusText}
                    </div>
                  ) : null}
                </div>
              </Styled>
            )}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}